/**
 * Edit a center's configuration. Loads the center, prefills the form, saves
 * back to the database as desired state. Identity and ports are fixed — a port
 * change is a migration, not an edit — so the form shows them read-only. The
 * domain is fixed too: it is the doctor id (Contract A), so it travels back
 * unchanged and the server refuses any other value.
 *
 * ── Compaction pass ─────────────────────────────────────────────────────────
 * The header's paragraph is gone; the form's own callout above Save already
 * says that saving stores desired state and that Plan is what applies it.
 * The `centers-form` wrapper is the same styling hook CenterNewPage adds, so
 * both routes get the compact grid from `page-centers.css`.
 */

import { useNavigate, useParams } from 'react-router-dom';

import { CenterForm, type CenterFormValue } from '../components/CenterForm';
import { PageHeader } from '../components/PageHeader';
import { ErrorBlock, SkeletonRows } from '../components/States';
import { useResource } from '../lib/useResource';
import type { CenterDetail } from '../types/api';

import '../styles/page-centers.css';

function toFormValue(c: CenterDetail): CenterFormValue {
  return {
    name: c.name ?? '',
    domain: c.domain,
    region: c.region ?? '',
    cache_ram_mb: c.cache_ram_mb,
    dicom_always_allow_find: c.dicom_always_allow_find,
    dicom_always_allow_get: c.dicom_always_allow_get,
    dicom_always_allow_move: c.dicom_always_allow_move,
    modalities: c.modalities.map((m) => ({
      symbolic_name: m.symbolic_name,
      aet: m.aet,
      host: m.host,
      port: m.port,
    })),
    date_restrictions: c.date_restrictions.map((r) => ({
      aet: r.aet,
      mode: r.mode as CenterFormValue['date_restrictions'][number]['mode'],
      days: r.days,
      start_date: r.start_date,
      end_date: r.end_date,
    })),
    label_restrictions: c.label_restrictions.map((r) => ({
      aet: r.aet,
      labels: r.labels,
      constraint: r.constraint as CenterFormValue['label_restrictions'][number]['constraint'],
    })),
    peers: c.peers.map((p) => ({
      peer_name: p.peer_name,
      target_slug: p.target_slug,
      url_env: p.url_env,
      username_env: p.username_env ?? null,
      password_env: p.password_env ?? null,
      http_timeout: p.http_timeout,
    })),
  };
}

export function CenterEditPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const center = useResource<CenterDetail>(`/api/centers/${encodeURIComponent(slug)}`);

  return (
    <>
      <PageHeader title={`Edit ${slug}`} />
      {center.error && !center.data ? (
        <ErrorBlock error={center.error} onRetry={center.reload} what="this center" />
      ) : !center.data ? (
        <SkeletonRows rows={5} />
      ) : (
        <div className="centers-form">
          <CenterForm
            mode="edit"
            slug={slug}
            initial={toFormValue(center.data)}
            onSaved={() => navigate(`/centers/${slug}`)}
          />
        </div>
      )}
    </>
  );
}
